import { useAuthStore } from "../stores/authStore";
import { supabase } from "./supabase";

/** Standard envelope returned by seasonality-query Edge Function */
export interface EdgeResponse<T> {
  data: T;
  count?: number;
  error?: string;
}

const SESSION_ERROR_PATTERN = /invalid token|session expired/i;

/**
 * Detects if a seasonality-query response/exception indicates an expired session.
 */
function isSessionExpired(error: unknown): boolean {
  const status =
    (error as { status?: number })?.status ??
    (error as { context?: { response?: { status?: number } } })?.context?.response?.status;

  const message =
    typeof error === "string"
      ? error
      : typeof (error as { message?: string })?.message === "string"
        ? (error as { message: string }).message
        : "";

  const matchesMessage = message ? SESSION_ERROR_PATTERN.test(message) : false;

  return status === 401 || matchesMessage;
}

/**
 * Handles auth/session errors by logging the user out with a consistent message.
 * Returns true if the error was handled as a session expiry.
 */
function handleSessionExpiry(error: unknown): boolean {
  if (!isSessionExpired(error)) return false;

  const logout = useAuthStore.getState().logout;
  logout("TOKEN_EXPIRED");
  return true;
}

/**
 * Standardises seasonality-query responses, handling session expiry automatically.
 */
export function ensureSeasonalitySuccess<T>(data: unknown, error: unknown): T {
  if (error) {
    if (handleSessionExpiry(error)) {
      throw new Error("La sessione è scaduta. Effettua nuovamente l'accesso dal Portale Operatori.");
    }
    const msg = (error as { message?: string })?.message || "Errore durante la richiesta";
    throw new Error(msg);
  }

  const responseData = data as Record<string, unknown> | null;
  if (responseData?.error) {
    if (handleSessionExpiry(responseData.error)) {
      throw new Error("La sessione è scaduta. Effettua nuovamente l'accesso dal Portale Operatori.");
    }
    throw new Error(String(responseData.error) || "Query error");
  }

  // Edge function returns { data, count } structure
  // Return the entire response object so hooks can access both data and count
  return data as T;
}

/**
 * Protected API call to seasonality-query Edge Function
 */
export async function querySeasonalityProtected<T>(
  token: string | null,
  action: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<T> {
  // For public actions, token is optional
  const publicActions = ["metrics", "all-regions", "low-season", "region-analytics"];
  const requiresAuth = !publicActions.includes(action);

  if (requiresAuth && !token) {
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {};
  if (token) {
    headers["X-Portale-Token"] = token;
  }

  const { data, error } = await supabase.functions.invoke("seasonality-query", {
    body: { action, ...params },
    headers,
    signal,
  });

  return ensureSeasonalitySuccess<T>(data, error);
}

/**
 * Public API call to seasonality-query Edge Function (no auth required)
 */
export async function querySeasonalityPublic<T>(
  action: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<T> {
  const { data, error } = await supabase.functions.invoke("seasonality-query", {
    body: { action, ...params },
    signal,
  });

  return ensureSeasonalitySuccess<T>(data, error);
}

